// Standard library imports
import React, { useContext, useState, useEffect } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { UilTrash, UilPen } from '@iconscout/react-unicons';

// Internal module imports
import Table from '../../../components/Table/Table';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import Typography from '../../../components/Typography/Typography';
import { DropdownComponent, Input, ToggleButton, ToggleButtonWithState } from '../../../components/Inputs/Input';
import { USER_ROLE } from '../../../constants';
import { AuthContext } from '../../../context/AuthContext';
import _, { get as LodashGet } from 'lodash';
import CreateButton from '../../../components/Buttons/CreateButton';
import { LoaderContext } from '../../../context/LoaderContext';
import { START_LOADER, STOP_LOADER } from '../../../constants';
import { CreateTagValidation } from '../../../validations/SCValidation/TagValidation';
import DeleteModalComponent from '../../../components/DeleteModal/DeleteModal';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import Dropdown from '../../../components/Inputs/Dropdown';

// Css module imports
import classes from '../../../styles/Alerts.module.css';
import buttonClasses from '../../../components/Buttons/Button.module.css';
import myClasses from '../../../styles/AllDevices.module.css';
import scClasses from '../SmartCharging.module.css';
import tagClasses from './Tags.module.css';
import { SCService } from '../../../services/SCService';

const LIMIT = 15;


const initialValues = {
    name: '',
    tagID: '',
    status: false,
    groupId: '',
    chargingType: 'tagId',
    vehicleBatteryCap: '',
    vehicleChargingCap: '',
};

const defaultValue = {
    label: 'All',
    value: 'All',
};

const Tags = ({ updateQueryParams, queryParamsData, sites = [] }) => {
    const { dispatch: loaderDispatch } = useContext(LoaderContext);
    const startLoader = (payload) => loaderDispatch({ type: START_LOADER, payload });
    const stopLoader = (payload) => loaderDispatch({ type: STOP_LOADER, payload });

    const { state } = useContext(AuthContext);
    const userRole = LodashGet(state, 'user.userRole');
    const [open, setOpen] = useState(queryParamsData.createTagModal ? queryParamsData.createTagModal : false);
    const [tableData, setTableData] = useState([]);
    const [deleteModal, setDeleteModal] = useState({ status: false, id: null });
    const [groups, setGroups] = useState([]);
    const [filterGroups, setFilterGroups] = useState([defaultValue]);
    const [selectedGroup, setSelectedGroup] = useState(defaultValue);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [tagFormValue, setTagFormValue] = useState(initialValues);

    const chargingTypeOptions = [{ label: 'Tag ID', value: 'tagId' }];

    useEffect(() => {
        getAllTags();
    }, [skip, selectedGroup?.value]);

    useEffect(() => {
        setGroups(sites);
        setFilterGroups([defaultValue, ...sites]);
    }, [sites]);

    useEffect(() => {
        updateQueryParams('createTagModal', open);
    }, [open]);

    const handleSubmit = (data) => {
        const formData = {
            name: data.name,
            tagID: data.tagID,
            status: data.status,
            ...(data?.groupId ? { groupId: data?.groupId } : {}),
            chargingType: data.chargingType,
            vehicleBatteryCap: data.vehicleBatteryCap,
            vehicleChargingCap: data.vehicleChargingCap,
        };

        if (data._id) {
            SCService.UpdateTag(
                data._id,
                formData,
                () => startLoader('updateTag'),
                () => handleCreateSuccess('Updated'),
                handleError,
                () => stopLoader('updateTag')
            );
        } else {
            SCService.CreateTag(
                formData,
                () => startLoader('createTag'),
                () => handleCreateSuccess('Created'),
                handleError,
                () => stopLoader('createTag')
            );
        }
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handleCreateSuccess = (type) => {
        getAllTags();
        setOpen(false);
        toast.success(`Tag ${type} Successfully`);
    };

    const getAllTags = () => {
        SCService.GetAllTags(
            {
                limit: LIMIT,
                skip,
                groupId: selectedGroup.value,
            },
            () => startLoader('getTags'),
            handleGetTagsSuccess,
            handleError,
            () => stopLoader('getTags')
        );
    };

    const handleTagToggleChange = (active, item) => {
        item.status = active;
        handleSubmit(item);
    };

    const getActive = (item) => {
        return (
            <div className={classes.ActionIcons}>
                <ToggleButtonWithState value={item.status} onChange={(e) => handleTagToggleChange(e.target.checked, item)} warpperStyle={{ marginTop: '0' }} />
            </div>
        );
    };

    const getActions = (item) => {
        return (
            <div className={classes.ActionIcons}>
                <div
                    onClick={() => {
                        setOpen(true);
                        setTagFormValue(item);
                    }}
                >
                    <CustomTooltip content={'Edit'}>
                        <UilPen className={buttonClasses.ButtonIcon} />
                    </CustomTooltip>
                </div>
                <div onClick={() => setDeleteModal({ status: true, id: item._id })}>
                    <CustomTooltip content={'Delete'}>
                        <UilTrash className={buttonClasses.ButtonIcon} />
                    </CustomTooltip>
                </div>
            </div>
        );
    };

    const handleGetTagsSuccess = ({ data }) => {
        const { tags, totalCount } = data?.data;
        const processedData = tags?.map((item, index) => {
            return {
                ...item,
                slNo: index + 1 + skip,
                group: item.groupData[0]?.name ? item.groupData[0].name : '--',
                active: getActive(item),
                action: getActions(item),
            };
        });

        setTableData(processedData);
        setTotalCount(_.get(_.head(totalCount), 'totalCount', 0));
    };

    const handleDeleteSuccess = (data, id) => {
        toast.success('Tag Deleted!');
        getAllTags();
    };

    const handleDelete = (id) => {
        SCService.DeleteTag(
            id,
            () => startLoader('deleteTag'),
            handleDeleteSuccess,
            handleError,
            () => stopLoader('deleteTag')
        );
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div className={classes.Alert}>
            <div className={classes.TableHeadSection}>
                <div className={scClasses.HeadingContainer}>
                    <Typography content="Tags" />
                    <div className={myClasses.TableCount}>
                        Total Count :
                        <span>
                            <Typography size="14" content={totalCount} />
                        </span>
                    </div>
                    <div className={scClasses.DropDnContainer}>
                        <DropdownComponent name="groupId" options={filterGroups} defaultValue={filterGroups.length ? filterGroups[0] : {}} onChange={(e) => setSelectedGroup(e)} />
                    </div>
                </div>
                {[USER_ROLE.SUDOER, USER_ROLE.EXPERT].includes(userRole) ? (
                    <div className={classes.CreateButton}>
                        <CreateButton
                            size="medium"
                            text="Create Tag"
                            onClick={() => {
                                setTagFormValue(initialValues);
                                setOpen(!open);
                            }}
                        />
                    </div>
                ) : null}
            </div>

            <Table
                head={[
                    'S. No.',
                    'Name',
                    'Tag Id',
                    'Site',
                    [USER_ROLE.SUDOER, USER_ROLE.EXPERT].includes(userRole) ? 'Active' : '',
                    [USER_ROLE.SUDOER, USER_ROLE.EXPERT].includes(userRole) ? 'Action' : '',
                ]}
                keys={[
                    'slNo',
                    'name',
                    'tagID',
                    'group',
                    [USER_ROLE.SUDOER, USER_ROLE.EXPERT].includes(userRole) ? 'active' : '',
                    [USER_ROLE.SUDOER, USER_ROLE.EXPERT].includes(userRole) ? 'action' : '',
                ]}
                data={tableData}
                page={page}
                Pagination={true}
                limit={LIMIT}
                handlePageChange={handlePageChange}
                totalCount={totalCount}
            />

            <ModalComponent isOpen={open} setOpen={setOpen} onClose={() => setOpen(false)}>
                <div className={tagClasses.ModalWidth} style={{ 'overflow-y': 'auto' }}>
                    <Formik
                        initialValues={tagFormValue}
                        validationSchema={() => CreateTagValidation()}
                        onSubmit={(val, { resetForm }) => {
                            handleSubmit(val, resetForm);
                        }}
                    >
                        {({ errors, touched, values, isValidating, ...props }) => {
                            return (
                                <Form>
                                    <Typography content="Create Tag" />
                                    <div>
                                        <div className={classes.InputContainer}>
                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="name">
                                                    Name <span className="required">*</span>
                                                </label>
                                                <Input name="name" id="name" />
                                            </div>

                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="tagID">
                                                    Tag ID <span className="required">*</span>
                                                </label>
                                                <div>
                                                    <Input name="tagID" id="tagID" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={classes.InputContainer}>
                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="groupId">Sites</label>
                                                <Dropdown
                                                    name="groupId"
                                                    options={groups}
                                                    defaultValue={
                                                        values?.groupId &&
                                                        groups.filter((group) => group.value === values?.groupId).length &&
                                                        groups.filter((group) => group.value === values?.groupId)[0]
                                                    }
                                                />
                                            </div>

                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <ToggleButton label={'Active'} values={values} name="status" id="action" />
                                            </div>
                                        </div>
                                        <div className={classes.InputContainer}>
                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="chargingType">
                                                    Charging Type <span className="required">*</span>
                                                </label>
                                                <Dropdown name="chargingType" options={chargingTypeOptions} defaultValue={chargingTypeOptions[0]} />
                                            </div>
                                        </div>

                                        <div className={classes.InputContainer}>
                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="vehicleBatteryCap">
                                                    Vehicle Battery Capacity (KWh) <span className="required">*</span>
                                                </label>
                                                <Input name="vehicleBatteryCap" id="vehicleBatteryCap" />
                                            </div>

                                            <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                <label for="vehicleChargingCap">
                                                    Vehicle Charging Capacity (kW)<span className="required">*</span>
                                                </label>
                                                <div>
                                                    <Input name="vehicleChargingCap" id="vehicleChargingCap" />
                                                </div>
                                            </div>
                                        </div>


                                        <div className={classes.ButtonContainer}>
                                            <div>
                                                <div
                                                    className="btn-secondary"
                                                    onClick={() => {
                                                        setOpen(false);
                                                    }}
                                                >
                                                    Cancel
                                                </div>
                                            </div>
                                            <div>
                                                <button type="submit" className="btn-primary">
                                                    Submit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </ModalComponent>

            <DeleteModalComponent
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ id: '', status })}
                deviceId={deleteModal.id}
            ></DeleteModalComponent>
        </div>
    );
};

export default Tags;
